import { useCallback, useEffect, useRef } from 'react';
import { useSetRecoilState } from 'recoil';
import { Job, jobQueueExpandedState } from '~/state/atoms/jobAtoms';
import { useJobManagement } from '../useJobManagement';
import { downloadConnectorResults, pollTaskStatus } from './utils';

/**
 * A hook for orchestrating connector tasks.
 * It processes tasks in the queue, handling upload, polling, and downloading results.
 * @returns {void}
 */
export function useConnectorJobOrchestrator() {
    const { jobQueue, setJobStatus, updateJob } = useJobManagement();
    const expandJobQueue = useSetRecoilState(jobQueueExpandedState);
    const currentlyProcessingJobIdsRef = useRef<Set<string>>(new Set());

    const processJob = useCallback(
        async (jobToProcess: Job) => {
            const jobId = jobToProcess.id;
            try {
                const pollData = await pollTaskStatus(
                    jobToProcess,
                    setJobStatus
                );

                if (pollData.status === 'Done') {
                    setJobStatus(jobId, 'Downloading');
                    const results = await downloadConnectorResults(pollData);

                    if (results.errors.length > 0) {
                        setJobStatus(jobId, 'Error', results.errors.join(', '));
                    } else {
                        updateJob(jobId, {
                            status: 'Done',
                            results: results.files,
                        });
                    }
                } else if (pollData.status === 'Error') {
                    setJobStatus(jobId, 'Error', 'Task failed on backend');
                } else if (pollData.status === 'Canceled') {
                    setJobStatus(jobId, 'Error', 'Task canceled');
                }
            } catch (error) {
                console.error(
                    `Orchestrator: Error during upload phase for job ${jobId}:`,
                    error
                );
                if (typeof error === 'string') {
                    setJobStatus(jobId, 'Error', error);
                } else if (error instanceof Error) {
                    setJobStatus(jobId, 'Error', error.message);
                } else {
                    setJobStatus(jobId, 'Error', 'Error processing task.');
                }
            }
        },
        [setJobStatus, updateJob]
    );

    useEffect(() => {
        const jobsToProcess = jobQueue.jobs.filter(
            (job) =>
                job.status === 'Queued' &&
                !currentlyProcessingJobIdsRef.current.has(job.id)
        );
        if (jobsToProcess.length > 0) {
            expandJobQueue(true);
        }
        jobsToProcess.forEach((job) => {
            currentlyProcessingJobIdsRef.current.add(job.id);
            processJob(job).finally(() => {
                currentlyProcessingJobIdsRef.current.delete(job.id);
            });
        });
    }, [jobQueue.jobs, processJob, setJobStatus]);
}
